import React, { useEffect, useState, useRef } from "react";
import ExcelJS from 'exceljs'; // Replace XLSX import
import { useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import { ReactComponent as DownloadIcon } from "../../pages/SearchResults/images/requestdownloadicon.svg";
import "./ClientSideExcelExporter.scss";

interface ClientSideExcelExporterProps {
  buttonTitle: string;
  searchData: any;
  apiPayload: any;
  recordsCounter: number;
}

const ClientSideExcelExporter: React.FC<ClientSideExcelExporterProps> = ({
  buttonTitle,
  searchData,
  apiPayload,
  recordsCounter,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [hitCount, setHitCount] = useState<number | null>(null);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  // Get toggle state from Redux store
  const toggleState = useSelector((state: any) => state.toggleManuals);
  const { isToggled } = toggleState;

  // Convert isToggled boolean to a string identifier for consistency
  const currentToggleState = isToggled ? 'manual' : 'normal';

  // FIX: Initialize with the current toggle state string, not the object
  const [previousToggleState, setPreviousToggleState] = useState<string>(currentToggleState);

  // Add a ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Storage keys for persisting export state
  const EXPORT_STATE_KEY = 'excel_export_in_progress';
  const EXPORT_PROGRESS_KEY = 'excel_export_progress';
  const EXPORT_ID_KEY = 'excel_export_id';
  const EXPORT_CANCELLED_KEY = 'excel_export_cancelled';
  const EXPORT_TIMESTAMP_KEY = 'excel_export_timestamp';

  // Get fresh Redux state directly in the component
  const freshReduxState = useSelector((state: any) => {
    return state.filteredFacets.selectedFactes;
  });

  // New effect to detect toggle state changes and stop download
  useEffect(() => {
    // Check if toggle state has changed and we're currently exporting
    if (previousToggleState !== currentToggleState && isExporting) {
      // Stop the current export
      stopCurrentExport('Toggle state changed');
    }

    // Update the previous toggle state
    setPreviousToggleState(currentToggleState);
  }, [currentToggleState, isExporting]); 

// Update the stopCurrentExport function
  const stopCurrentExport = (reason: string) => {
    
    // Mark export as cancelled in localStorage
    localStorage.setItem(EXPORT_CANCELLED_KEY, 'true');

    // Clear export state
    localStorage.removeItem(EXPORT_STATE_KEY);
    localStorage.removeItem(EXPORT_PROGRESS_KEY);
    localStorage.removeItem(EXPORT_ID_KEY);
    localStorage.removeItem(EXPORT_TIMESTAMP_KEY); 

    // Dispatch custom event to notify all component instances
    window.dispatchEvent(new CustomEvent('exportCancelled', { detail: { reason } }));
    window.dispatchEvent(new CustomEvent('exportComplete'));

    // Reset component state only if component is still mounted
    if (isMountedRef.current) {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  // useEffect to handle page refresh detection
  useEffect(() => {
    // Set component as mounted
    isMountedRef.current = true;

    // Check if there was an ongoing export before page refresh
    const checkForPageRefresh = () => {
      const exportInProgress = localStorage.getItem(EXPORT_STATE_KEY);
      const exportTimestamp = localStorage.getItem(EXPORT_TIMESTAMP_KEY);
      
      if (exportInProgress === 'true' && exportTimestamp) {
        const lastExportTime = parseInt(exportTimestamp);
        const currentTime = Date.now();
        const timeDifference = currentTime - lastExportTime;
        
        // If more than 5 seconds have passed since last update, consider it a page refresh
        if (timeDifference > 5000) {
          stopCurrentExport('Page was refreshed');
        }
      }
    };

    checkForPageRefresh();

    // Handle beforeunload event to detect page refresh/close
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const exportInProgress = localStorage.getItem(EXPORT_STATE_KEY);
      
      if (exportInProgress === 'true') {
        
        // Stop the export immediately
        stopCurrentExport('Page unload detected');
        
      }
    };

    // Handle visibility change (when user switches tabs or minimizes browser)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Update timestamp when page becomes hidden
        const exportInProgress = localStorage.getItem(EXPORT_STATE_KEY);
        if (exportInProgress === 'true') {
          localStorage.setItem(EXPORT_TIMESTAMP_KEY, Date.now().toString());
        }
      } else {
        // Check if export should be stopped when page becomes visible again
        const exportInProgress = localStorage.getItem(EXPORT_STATE_KEY);
        const exportTimestamp = localStorage.getItem(EXPORT_TIMESTAMP_KEY);
        
        if (exportInProgress === 'true' && exportTimestamp) {
          const lastExportTime = parseInt(exportTimestamp);
          const currentTime = Date.now();
          const timeDifference = currentTime - lastExportTime;
          
          // If more than 10 seconds have passed, stop the export
          if (timeDifference > 10000) {
            stopCurrentExport('Export timeout after page visibility change');
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Check for existing export state on component mount
  useEffect(() => {
    const checkExistingExport = () => {

      const existingExport = localStorage.getItem(EXPORT_STATE_KEY);
      const existingProgress = localStorage.getItem(EXPORT_PROGRESS_KEY);
      const exportCancelled = localStorage.getItem(EXPORT_CANCELLED_KEY);
      const exportTimestamp = localStorage.getItem(EXPORT_TIMESTAMP_KEY);


      // If export was cancelled, clear all state
      if (exportCancelled === 'true') {
        localStorage.removeItem(EXPORT_CANCELLED_KEY);
        localStorage.removeItem(EXPORT_STATE_KEY);
        localStorage.removeItem(EXPORT_PROGRESS_KEY);
        localStorage.removeItem(EXPORT_ID_KEY);
        localStorage.removeItem(EXPORT_TIMESTAMP_KEY);
        setIsExporting(false);
        setExportProgress({ current: 0, total: 0 });
        return;
      }
      // Check for stale export (page refresh detection)
      if (existingExport === 'true' && exportTimestamp) {
        const lastExportTime = parseInt(exportTimestamp);
        const currentTime = Date.now();
        const timeDifference = currentTime - lastExportTime;
        
        // If more than 30 seconds have passed, consider it stale
        if (timeDifference > 30000) {
          localStorage.removeItem(EXPORT_STATE_KEY);
          localStorage.removeItem(EXPORT_PROGRESS_KEY);
          localStorage.removeItem(EXPORT_ID_KEY);
          localStorage.removeItem(EXPORT_TIMESTAMP_KEY);
          setIsExporting(false);
          setExportProgress({ current: 0, total: 0 });
          return;
        }
      }

      if (existingExport === 'true') {
        setIsExporting(true);
        if (existingProgress) {
          try {
            const progress = JSON.parse(existingProgress);
            setExportProgress(progress);
          } catch (error) {
            setExportProgress({ current: 0, total: 0 });
          }
        }
      } else {
        setIsExporting(false);
        setExportProgress({ current: 0, total: 0 });
      }
    };

    checkExistingExport();

    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {

      if (e.key === EXPORT_STATE_KEY) {
        if (e.newValue === 'false' || e.newValue === null) {
          setIsExporting(false);
          setExportProgress({ current: 0, total: 0 });
        } else if (e.newValue === 'true') {
          setIsExporting(true);
        }
      }
      if (e.key === EXPORT_PROGRESS_KEY && e.newValue) {
        try {
          const progress = JSON.parse(e.newValue);
          setExportProgress(progress);
        } catch (error) {
          console.warn('[ClientSideExcelExporter] Error parsing export progress from storage:', error);
        }
      }
    };

    // Listen for custom export completion and cancellation events
    const handleExportComplete = () => {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    };

    const handleExportCancelled = (event: CustomEvent) => {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('exportComplete', handleExportComplete);
    window.addEventListener('exportCancelled', handleExportCancelled as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('exportComplete', handleExportComplete);
      window.removeEventListener('exportCancelled', handleExportCancelled as EventListener);
    };
  }, []);

  // Reset hitCount when filters change
  useEffect(() => {
    setHitCount(null);
  }, [freshReduxState]);

  const exportToExcel = async () => {
    if (isExporting) {
      return;
    }

    if (!searchData || !searchData.hits || !searchData.hits.hits.length) {
      return;
    }
    // Store the current toggle state at the start of export
    const initialToggleState = currentToggleState;

    // Generate unique export ID
    const exportId = Date.now().toString();
    const startTimestamp = Date.now().toString();

    // Clear any previous cancellation flag
    localStorage.removeItem(EXPORT_CANCELLED_KEY);

    // Set export state in localStorage and component state
    localStorage.setItem(EXPORT_STATE_KEY, 'true');
    localStorage.setItem(EXPORT_ID_KEY, exportId);
    localStorage.setItem(EXPORT_TIMESTAMP_KEY, startTimestamp);
    setIsExporting(true);
    setExportProgress({ current: 0, total: 0 });
    localStorage.setItem(EXPORT_PROGRESS_KEY, JSON.stringify({ current: 0, total: 0 }));

    try {
      // Small delay to ensure Redux state is updated
      await new Promise(resolve => setTimeout(resolve, 100));

      const currentApiPayload = apiPayload;

      // Check specifically for manufacturers facet
      const hasManufacturersFacet = currentApiPayload?.selectedFacets?.some((facet: any) =>
        facet.facetName === "manufacturers" ||
        facet.facetField === "manufacturers.aircraft_oem" ||
        facet.facetField === "manufacturers.aircraft_model" ||
        facet.facetName === "publication_category_name" ||
        facet.facetName === "product_family"
      );

      // Check if range is present in the payload
      const hasRangeInPayload = currentApiPayload?.range !== undefined && currentApiPayload?.range !== null;

      // Start with current search data
      let allHits = searchData.hits.hits;

      try {
        const totalAvailable = searchData.hits.total?.value;

        setHitCount(totalAvailable);

        let pageSize = 2000;
        if (hasRangeInPayload) {
          pageSize = 500; // Use 500 when range is present
        } else if (hasManufacturersFacet) {
          pageSize = 500; // Use 500 for manufacturers facet
        }
        const totalPages = Math.ceil(totalAvailable / pageSize);
        // Array to store all fetched records with metadata
        const allFetchedHits: any[] = [];

        for (let pageNo = 1; pageNo <= totalPages; pageNo++) {
          // Check if export was cancelled
          const exportCancelled = localStorage.getItem(EXPORT_CANCELLED_KEY);
          if (exportCancelled === 'true') {
            return;
          }
          // Check if component is still mounted
        if (!isMountedRef.current) {
          stopCurrentExport('Component unmounted');
          return;
        }
        // Check if toggle state has changed during export
          if (currentToggleState !== initialToggleState) {
            return;
          }

          // Check if this export is still the current one
          const currentExportId = localStorage.getItem(EXPORT_ID_KEY);
          if (currentExportId !== exportId) {
            return;
          }
          
          // Update timestamp to show activity
        localStorage.setItem(EXPORT_TIMESTAMP_KEY, Date.now().toString());

          const currentProgress = { current: pageNo, total: totalPages };
          setExportProgress(currentProgress);
          localStorage.setItem(EXPORT_PROGRESS_KEY, JSON.stringify(currentProgress));

          const startRecord = (pageNo - 1) * pageSize + 1;
          const endRecord = Math.min(pageNo * pageSize, totalAvailable);
          const actualPageSize = endRecord - startRecord + 1;

          try {
            
            let exportPayload: any;

            if (initialToggleState === 'manual') {
              // Manual mode: Create special payload for manual mode
              exportPayload = await createManualModeExportPayload(currentApiPayload, pageNo, actualPageSize);
            } else {
              // Normal mode: Use regular payload
              exportPayload = {
                ...currentApiPayload,
                pageable: {
                  pageNo: pageNo.toString(),
                  pageSize: actualPageSize.toString(),
                  sortBy: currentApiPayload.pageable?.sortBy || "",
                  sortDir: currentApiPayload.pageable?.sortDir || ""
                }
              };
            }

            // Check again before making the API call
            if (currentToggleState !== initialToggleState) {
              return;
            }
            const response = await fetch("/api/v1/search/search-results", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(exportPayload),
            });
            // Check again after API call
            if (currentToggleState !== initialToggleState) {
              return;
            }
            if (response.ok) {
              const data = await response.json();

              if (data?.message?.isSuccess === true && data.message.result?.hits?.hits) {
                const pageHits = data.message.result.hits.hits;
                allFetchedHits.push(...pageHits);
              } else {
                const errorMsg = `Invalid response structure: ${JSON.stringify(data?.message)}`;
              }
            } else {
              const errorText = await response.text();
              const errorMsg = `HTTP ${response.status}: ${errorText}`;
              console.error(`[ClientSideExcelExporter] Page ${pageNo} HTTP error:`, errorMsg);
            }

            localStorage.setItem(EXPORT_TIMESTAMP_KEY, Date.now().toString());

            // Delay between requests with cancellation check
            if (pageNo < totalPages) {
              await new Promise((resolve) => {
                const timeoutId = setTimeout(() => {
                  // Check one more time before resolving
                  if (currentToggleState === initialToggleState) {
                    resolve(void 0);
                  }
                }, 100);
                
                // If toggle state changed during delay, clear timeout and resolve
                if (currentToggleState !== initialToggleState) {
                  clearTimeout(timeoutId);
                  resolve(void 0);
                }
              });
            }

          } catch (pageError) {
            const errorMsg = `Network/Parse Error: ${pageError instanceof Error ? pageError.message : String(pageError)}`;
            console.error(`[ClientSideExcelExporter] Page ${pageNo} error:`, errorMsg);
          }
        }


        if (allFetchedHits.length > 0) {
          allHits = allFetchedHits;
        } else {
          console.warn("[ClientSideExcelExporter] No records fetched from API calls, using current page data");
        }

      } catch (error) {
        console.warn("[ClientSideExcelExporter] API calls failed, using current page data:", error);
      }
       // Check one more time if toggle state changed before processing Excel
      if (currentToggleState !== initialToggleState) {
        return;
      }

      // Check one more time if export was cancelled before processing Excel
      const exportCancelled = localStorage.getItem(EXPORT_CANCELLED_KEY);
      if (exportCancelled === 'true') {
        return;
      }

      const truncateText = (text: string, maxLength = 32767): string => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength - 3) + '...';
      };

      const formatDate = (dateString: string): string => {
        if (!dateString || dateString === 'N/A') return 'N/A';

        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;

          const day = date.getDate();
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[date.getMonth()];
          const year = date.getFullYear().toString().slice(-2);

          return `${day}-${month}-${year}`;
        } catch (error) {
          console.warn('[ClientSideExcelExporter] Date formatting error:', error);
          return dateString;
        }
      };

      // Enhanced Excel data mapping with more fields
      const excelData = allHits.map((hit: any, index: number) => {
        const source = hit._source || {};

        const safeJoinArray = (arr: any[], maxLength = 32767): string => {
          if (!Array.isArray(arr) || arr.length === 0) return '';
          const joined = arr.join(', ');
          return truncateText(joined, maxLength);
        };

        return {
          'ATANUMBER': truncateText(source.ata || 'N/A'),
          'PUBNUMBER': truncateText(source.pub_number || 'N/A'),
          'VOLUME': source.publication_volume || 'N/A',
          'TITLE': truncateText(source.title || 'N/A'),
          'REVNUMBER': truncateText(source.version_number || 'N/A'),
          'REVDATE': source.revision_date || 'N/A',
          'PUBLICATIONTYPE': truncateText(source.publication_type_name || 'N/A'),
          'TRINDICATOR': 'N/A',
          'PART NUMBERS': safeJoinArray(source.part_numbers),
          'MODEL UNIT': truncateText(source.related_products?.[0] || ''),
          'APPLICATIONS': safeJoinArray(source.related_applications) || 'N/A',
          'ORIGINAL ISSUE DATE': source.original_date || 'N/A',
          'ORGANIZATION': truncateText(source.org_desc || 'N/A'),
          'RELATED PUBLICATIONS': safeJoinArray(source.related_publications) || 'N/A',
          'PRODUCT FAMILY': safeJoinArray(
            Array.isArray(source.product_family) ?
              source.product_family.map((pf: any) => pf.familyname) : []
          ),
          'AIRCRAFT MANUFACTURER': safeJoinArray(
            Array.isArray(source.manufacturers) ?
              source.manufacturers.map((mfg: any) => mfg.aircraft_oem) : []
          ),
          'ISSUE NUMBER': truncateText(source.pub_uuid?.toString() || ''),
        };
      });

      // Create Excel using ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Filtered Search Results');

      // Define headers
      const headers = [
        'ATANUMBER', 'PUBNUMBER', 'VOLUME', 'TITLE', 'REVNUMBER', 'REVDATE',
        'PUBLICATIONTYPE', 'TRINDICATOR', 'PART NUMBERS', 'MODEL UNIT',
        'APPLICATIONS', 'ORIGINAL ISSUE DATE', 'ORGANIZATION', 'RELATED PUBLICATIONS',
        'PRODUCT FAMILY', 'AIRCRAFT MANUFACTURER', 'ISSUE NUMBER'
      ];

      // Add header row with styling
      const headerRow = worksheet.addRow(headers);

      // Style the header row
      headerRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFADD8E6' } // Light blue background
        };
        cell.font = {
          bold: true,
          color: { argb: 'FF000000' } // Black color
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle'
        };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Add data rows
      excelData.forEach(row => {
        const dataRow = [
          row['ATANUMBER'],
          row['PUBNUMBER'],
          row['VOLUME'],
          row['TITLE'],
          row['REVNUMBER'],
          row['REVDATE'],
          row['PUBLICATIONTYPE'],
          row['TRINDICATOR'],
          row['PART NUMBERS'],
          row['MODEL UNIT'],
          row['APPLICATIONS'],
          row['ORIGINAL ISSUE DATE'],
          row['ORGANIZATION'],
          row['RELATED PUBLICATIONS'],
          row['PRODUCT FAMILY'],
          row['AIRCRAFT MANUFACTURER'],
          row['ISSUE NUMBER']
        ];
        worksheet.addRow(dataRow);
      });

      // Set column widths
      const columnWidths = [15, 20, 10, 40, 12, 15, 25, 12, 30, 25, 50, 18, 35, 30, 30, 40, 15];
      columnWidths.forEach((width, index) => {
        worksheet.getColumn(index + 1).width = width;
      });

      // Create filename that includes facet selections
      const createFilenameWithFacets = (apiPayload: any): string => {
        let filename = 'search-results';

        // Add facet selections to filename
        if (apiPayload?.selectedFacets && apiPayload.selectedFacets.length > 0) {
          const facetValues: string[] = [];

          apiPayload.selectedFacets.forEach((facet: any) => {
            if (facet.facetsSelectedValue && facet.facetsSelectedValue.length > 0) {
              // Clean facet values for filename (remove special characters)
              const cleanValues = facet.facetsSelectedValue.map((value: string) =>
                value.replace(/[^a-zA-Z0-9]/g, '_')
              );
              facetValues.push(...cleanValues);
            }
          });

          if (facetValues.length > 0) {
            // Limit filename length and join facet values
            const facetString = facetValues.slice(0, 3).join('_'); // Limit to first 3 facets
            filename += `_${facetString}`;
          }
        }

        // Add date
        const date = new Date().toISOString().slice(0, 10);
        return `${filename}_${date}.xlsx`;
      };

      const filename = createFilenameWithFacets(currentApiPayload);

      // Final check if export was cancelled before download
      const finalCancelCheck = localStorage.getItem(EXPORT_CANCELLED_KEY);
      if (finalCancelCheck === 'true') {
        return;
      }

      // Generate Excel file and download
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("[ClientSideExcelExporter] Error exporting to Excel:", error);
    } finally {

      // Clear ALL export state from localStorage and component state
      localStorage.removeItem(EXPORT_STATE_KEY);
      localStorage.removeItem(EXPORT_PROGRESS_KEY);
      localStorage.removeItem(EXPORT_ID_KEY);
      localStorage.removeItem(EXPORT_CANCELLED_KEY);
      localStorage.removeItem(EXPORT_TIMESTAMP_KEY);

      // Dispatch custom event to notify all component instances
      window.dispatchEvent(new CustomEvent('exportComplete'));

      // Reset component state only if component is still mounted
      if (isMountedRef.current) {
        setIsExporting(false);
        setExportProgress({ current: 0, total: 0 });
      }

    }
  };
// helper function for manual mode export payload
  const createManualModeExportPayload = async (currentApiPayload: any, pageNo: number, pageSize: number) => {
    try {
      // Fetch all library data to get pubIds (similar to SearchResults component)
      const allLibraryData = await fetchAllLibraryDataForExport();
      const pubIdsString = extractPubIdsForExport(allLibraryData);

      // Create payload similar to manual mode in SearchResults
      const selectedFacets = [...(currentApiPayload?.selectedFacets || [])];
      
      // Always add Manual filter for manual mode
      const manualFilter = {
        facetName: "publication_category_name",
        facetField: "publication_category_name.publication_category",
        facetsSelectedValue: ["Manual"]
      };

      // Check if Manual filter already exists
      const existingManualFilter = selectedFacets.find(
        filter => filter.facetName === "publication_category_name"
      );

      if (existingManualFilter) {
        if (!existingManualFilter.facetsSelectedValue.includes("Manual")) {
          existingManualFilter.facetsSelectedValue.push("Manual");
        }
      } else {
        selectedFacets.push(manualFilter);
      }

      return {
        queryString: pubIdsString, // Use pubIds instead of search term
        pageable: {
          pageNo: pageNo.toString(),
          pageSize: pageSize.toString(),
          sortBy: currentApiPayload.pageable?.sortBy || "",
          sortDir: currentApiPayload.pageable?.sortDir || ""
        },
        selectedFacets: selectedFacets,
        // Include any additional filters
        ...(currentApiPayload?.fromDate && { fromDate: currentApiPayload.fromDate }),
        ...(currentApiPayload?.toDate && { toDate: currentApiPayload.toDate })
      };
    } catch (error) {
      console.error('Error creating manual mode export payload:', error);
      // Fallback to regular payload
      return {
        ...currentApiPayload,
        pageable: {
          pageNo: pageNo.toString(),
          pageSize: pageSize.toString(),
          sortBy: currentApiPayload.pageable?.sortBy || "",
          sortDir: currentApiPayload.pageable?.sortDir || ""
        }
      };
    }
  };

  // Add helper functions for library data
  const fetchLibraryDataForExport = async (pageNumber = 1, pageSize = 100) => {
    try {
      const response = await fetch(`/api/v1/library`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pageNo: pageNumber,
          pageSize: pageSize,
          searchParameter: "",
        }),
      });
      const data = await response.json();

      if (data?.message?.isSuccess === true && data?.message?.result?.result !== undefined) {
        return {
          data: data?.message?.result?.result,
          totalRecords: data?.message?.result?.totalRecordsFromBMS || 0,
          success: true
        };
      } else {
        return {
          data: [],
          totalRecords: 0,
          success: true
        };
      }
    } catch (error) {
      console.error('Library API error in export:', error);
      throw error;
    }
  };

  const fetchAllLibraryDataForExport = async () => {
    let allLibraryData: any[] = [];
    const pageSize = 100;

    try {
      const firstResponse = await fetchLibraryDataForExport(1, pageSize);
      allLibraryData = [...firstResponse.data];
      const totalRecords = firstResponse.totalRecords;
      const totalPages = Math.ceil(totalRecords / pageSize);

      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const response = await fetchLibraryDataForExport(page, pageSize);
          allLibraryData = [...allLibraryData, ...response.data];
        }
      }
      return allLibraryData;
    } catch (error) {
      console.error('Error fetching all library data for export:', error);
      throw error;
    }
  };

  const extractPubIdsForExport = (libraryData: any[]): string => {
    const pubIds = libraryData.map((item: any) =>
      item.pubId || item.publicationId || item.id
    );
    return pubIds.join(' ');
  };
  const getTooltipText = () => {
    if (isExporting) {
      if (exportProgress.total > 0) {
        return `Downloading... `;
      }
      return "Export in progress...";
    }
    if (hitCount === null) {
      return "Allows up to 10,000 items to be exported";
    }
    return "Allows up to 10,000 items to be exported";
  };
// FIX: Wrap disabled button with span to fix Tooltip warning
  const renderButton = () => {
    const button = (
      <button
        className={`btnDownload ${isExporting ? 'downloading' : ''}`}
        onClick={exportToExcel}
        disabled={isExporting}
      >
        <DownloadIcon className="downloadIcon" style={{ marginRight: 8 }} />
        <p className="text">
          {isExporting ? (
            <>
              Downloading
              <span className="loader__dot">.</span>
              <span className="loader__dot">.</span>
              <span className="loader__dot">.</span>
            </>
          ) : (buttonTitle)}
        </p>
      </button>
    );

    // If button is disabled, wrap it in a span for Tooltip to work properly
    if (isExporting) {
      return <span>{button}</span>;
    }

    return button;
  };
  return (
    <div className="clientExcelDownload">
      <Tooltip title={getTooltipText()} 
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#384348",
              fontFamily: "PP Telegraf",
              fontWeight: "400",
              color: "#FFFFFF",
            },
           },
          arrow: {
            sx: {
              color: "#384348",
            },
          },
        }}
      >
        {renderButton()}
      </Tooltip>
    </div>
  );
};

export default ClientSideExcelExporter;